import React, { useEffect, useRef, useState } from 'react';
import { Camera, AlertTriangle, X } from 'lucide-react';

const CameraMonitor = ({ onViolation, sessionId }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState(null);
  const [violationCount, setViolationCount] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [detectedObjects, setDetectedObjects] = useState([]);
  const detectionIntervalRef = useRef(null);
  const consecutiveDetectionsRef = useRef(0);
  const lastViolationTimeRef = useRef(0);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsActive(true);
        setError(null);

        // Start object detection after video is loaded
        videoRef.current.onloadedmetadata = () => {
          startObjectDetection();
        };
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setError('Camera access denied. Please allow camera access to continue.');
      setIsActive(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }
    setIsActive(false);
  };

  const startObjectDetection = () => {
    // Simple object detection based on motion and color changes
    // In production, you would use TensorFlow.js or similar for real object detection
    detectionIntervalRef.current = setInterval(() => {
      detectObjects();
    }, 8000); // Check every 8 seconds (less frequent)
  };

  const detectObjects = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Simple detection: check for multiple faces or unusual objects
    // This is a placeholder - in production use proper ML models
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const detected = analyzeFrame(imageData);

    if (detected.length > 0) {
      setDetectedObjects(detected);
      consecutiveDetectionsRef.current += 1;
      
      // Only trigger violation after 7 consecutive detections (56 seconds)
      // AND at least 90 seconds since last violation
      const timeSinceLastViolation = Date.now() - lastViolationTimeRef.current;
      if (consecutiveDetectionsRef.current >= 7 && timeSinceLastViolation > 90000) {
        handleViolation(detected);
        consecutiveDetectionsRef.current = 0;
      }
    } else {
      setDetectedObjects([]);
      consecutiveDetectionsRef.current = 0;
    }
  };

  const analyzeFrame = (imageData) => {
    // Placeholder for object detection
    // In production, integrate TensorFlow.js COCO-SSD or similar
    // For now, we'll do basic checks
    const detected = [];

    // Check for multiple bright spots (could indicate phones/screens)
    const data = imageData.data;
    let brightSpots = 0;
    
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const brightness = (r + g + b) / 3;
      
      if (brightness > 245) { // Higher threshold to reduce false positives
        brightSpots++;
      }
    }

    // If too many bright spots, might indicate a phone screen
    // Increased threshold to 45% to reduce false positives
    const brightRatio = brightSpots / (data.length / 4);
    if (brightRatio > 0.45) {
      detected.push({
        type: 'suspicious_object',
        confidence: 0.5,
        label: 'Possible unauthorized device detected'
      });
    }

    return detected;
  };

  const handleViolation = (objects) => {
    const newCount = violationCount + 1;
    setViolationCount(newCount);
    setShowWarning(true);
    lastViolationTimeRef.current = Date.now();

    if (onViolation) {
      onViolation({
        type: 'object_detection',
        objects: objects,
        count: newCount,
        timestamp: Date.now()
      });
    }

    // Auto-hide warning after 10 seconds
    setTimeout(() => {
      setShowWarning(false);
    }, 10000);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Camera Feed */}
      <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-2xl border-2 border-gray-700">
        <div className="relative w-48 h-36">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          <canvas ref={canvasRef} className="hidden" />
          
          {/* Status Indicator */}
          <div className="absolute top-2 left-2 flex items-center space-x-1">
            <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-red-500 animate-pulse' : 'bg-gray-500'}`} />
            <span className="text-xs text-white font-medium">
              {isActive ? 'LIVE' : 'OFF'}
            </span>
          </div>

          {/* Camera Icon */}
          <div className="absolute top-2 right-2">
            <Camera className="w-4 h-4 text-white" />
          </div>

          {/* Detected Objects Overlay */}
          {detectedObjects.length > 0 && (
            <div className="absolute inset-0 border-4 border-red-500 animate-pulse">
              <div className="absolute bottom-0 left-0 right-0 bg-red-500 bg-opacity-90 text-white text-xs p-1 text-center">
                ⚠️ Unauthorized Object Detected
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="absolute inset-0 bg-red-900 bg-opacity-90 flex items-center justify-center p-2">
              <p className="text-white text-xs text-center">{error}</p>
            </div>
          )}
        </div>

        {/* Violation Counter */}
        {violationCount > 0 && (
          <div className="absolute bottom-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded-full font-bold">
            Violations: {violationCount}/3
          </div>
        )}
      </div>

      {/* Warning Modal */}
      {showWarning && (
        <div className="absolute bottom-full right-0 mb-2 w-64 bg-red-50 border-2 border-red-500 rounded-lg p-3 shadow-xl">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-bold text-red-900 mb-1">
                Warning #{violationCount}
              </h4>
              <p className="text-xs text-red-800">
                Unauthorized object detected in camera view. Remove all devices immediately.
              </p>
              {violationCount >= 2 && (
                <p className="text-xs text-red-900 font-bold mt-1">
                  ⚠️ One more violation will auto-submit your test with 0 marks!
                </p>
              )}
            </div>
            <button
              onClick={() => setShowWarning(false)}
              className="text-red-600 hover:text-red-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CameraMonitor;
